/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, type KeyboardEvent, useEffect, useRef, useState } from 'react';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './ManufacturingJobs.module.scss';

type Translate = (key: string) => string;

type Demo =
  | { kind: 'io'; inValue: string; outValue: string; outNote: string }
  | { kind: 'chain'; nodes: { name: string; jurisdiction: string; pct?: string }[] }
  | { kind: 'screen'; badge: string; who: string; meta: string }
  | { kind: 'event'; code: string; when: string; meta: string };

type Job = {
  id: string;
  title: string;
  description: string;
  returned: string;
  demo: Demo;
};

const getJobs = (t: Translate): Job[] => [
  {
    id: 'resolve',
    title: t('Resolve the entity'),
    description: t(
      'Match a messy vendor string to a registered company. Trading names, transliterations, abbreviations and stale addresses all resolve to a registration number.',
    ),
    returned: t(
      'Legal name · registration number · registry of record · incorporation date · registered address · legal form · status · activity codes',
    ),
    demo: {
      kind: 'io',
      inValue: 'ACME PRECISION CMPNTS LTD',
      outValue: 'SAMPLE-09410808',
      outNote: 'Acme Precision Components Limited · Companies House',
    },
  },
  {
    id: 'map',
    title: t('Map the ownership'),
    description: t(
      'Walk from the plant you buy from to the company that controls it, across borders, through holding structures, to the ultimate beneficial owner where the register discloses one.',
    ),
    returned: t(
      'Shareholders and percentages · immediate and ultimate parent · subsidiaries · directors and officers · beneficial owners where disclosed',
    ),
    demo: {
      kind: 'chain',
      nodes: [
        { name: 'Acme Precision Components Ltd', jurisdiction: 'United Kingdom · Companies House' },
        { name: 'Meridian Industrial Holdings B.V.', jurisdiction: 'Netherlands · KVK', pct: 'holds 55%' },
        { name: 'Meridian Group AB', jurisdiction: 'Sweden · Bolagsverket', pct: 'holds 100%' },
      ],
    },
  },
  {
    id: 'screen',
    title: t('Screen the counterparty'),
    description: t(
      'Check the resolved entity and the people behind it against sanctions, restricted-party and politically exposed person lists. Screening runs against the registry identity, not the name on the invoice.',
    ),
    returned: t(
      'List matches with the source list named · match confidence · officer and owner level hits · date of the list version screened against',
    ),
    demo: {
      kind: 'screen',
      badge: 'Match',
      who: 'Officer level',
      meta: 'Source list OFSI consolidated · version 2026-08-01 · matched against the registry identity, not the vendor string',
    },
  },
  {
    id: 'watch',
    title: t('Watch for change'),
    description: t(
      'Registers move. Directors resign, shareholdings transfer, insolvency notices are filed, companies dissolve. Put a supplier under monitoring and the change reaches you as an event.',
    ),
    returned: t(
      'Status changes · officer appointments and resignations · shareholding transfers · insolvency and charge filings · address and name changes',
    ),
    demo: {
      kind: 'event',
      code: 'officer.resigned',
      when: 'filed 2026-07-22',
      meta: 'Entity SAMPLE-09410808 · Companies House · delivered to webhook and portfolio',
    },
  },
];

const JobDemo = ({ demo }: { demo: Demo }) => {
  const { t } = useInt();

  if (demo.kind === 'io') {
    return (
      <div className={styles.demoIo}>
        <div className={styles.ioRow}>
          <span className={styles.ioK}>{t('In')}</span>
          <span className={styles.ioIn}>{demo.inValue}</span>
        </div>
        <div className={styles.ioSep}>
          <span>{t('Matched to the register')}</span>
          <i />
        </div>
        <div className={styles.ioRow}>
          <span className={styles.ioK}>{t('Out')}</span>
          <span className={styles.ioOut}>
            {demo.outValue}
            <em>{demo.outNote}</em>
          </span>
        </div>
      </div>
    );
  }

  if (demo.kind === 'chain') {
    return (
      <div className={styles.demoChain}>
        {demo.nodes.map((node, i) => (
          <div className={styles.chainStep} key={node.name}>
            <div className={styles.chainNode}>
              <span className={styles.chainName}>{node.name}</span>
              <span className={styles.chainJur}>{node.jurisdiction}</span>
              {node.pct && <span className={styles.chainPct}>{node.pct}</span>}
            </div>
            {i < demo.nodes.length - 1 && <span className={styles.chainArrow}>&rarr;</span>}
          </div>
        ))}
      </div>
    );
  }

  if (demo.kind === 'screen') {
    return (
      <div className={styles.demoScreen}>
        <div className={styles.screenHit}>
          <span className={styles.screenBadge}>{demo.badge}</span>
          <span className={styles.screenWho}>{demo.who}</span>
        </div>
        <p className={styles.screenMeta}>{demo.meta}</p>
      </div>
    );
  }

  return (
    <div className={styles.demoEvent}>
      <div className={styles.eventRow}>
        <span className={styles.eventCode}>{demo.code}</span>
        <span className={styles.eventWhen}>{demo.when}</span>
      </div>
      <p className={styles.eventMeta}>{demo.meta}</p>
    </div>
  );
};

export const ManufacturingJobs = () => {
  const { t } = useInt();
  const jobs = getJobs(t);
  const N = jobs.length;
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const scrollWrapRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const stepTop = (index: number) => {
    const wrapEl = scrollWrapRef.current;
    const stickyEl = stickyRef.current;
    if (!wrapEl || !stickyEl) {
      return 0;
    }

    const travel = Math.max(1, wrapEl.offsetHeight - stickyEl.offsetHeight);
    const parsedTop = parseFloat(getComputedStyle(stickyEl).top);
    const stickyTop = Number.isNaN(parsedTop) ? 96 : parsedTop;
    return window.scrollY + wrapEl.getBoundingClientRect().top + (index / N) * travel - stickyTop + 8;
  };

  const pick = (index: number, jump = false) => {
    setActive(index);
    tabRefs.current[index]?.focus();

    if (jump && scrollWrapRef.current && stickyRef.current) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: stepTop(index), behavior: reduce ? 'auto' : 'smooth' });
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number | null = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      next = (index + 1) % N;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      next = (index - 1 + N) % N;
    }
    if (next !== null) {
      event.preventDefault();
      pick(next, true);
    }
  };

  useEffect(() => {
    const wrapEl = scrollWrapRef.current;
    const stickyEl = stickyRef.current;
    if (!wrapEl || !stickyEl || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    let running = true;

    const poll = () => {
      if (!running) {
        return;
      }

      if (getComputedStyle(stickyEl).position === 'sticky') {
        const parsedTop = parseFloat(getComputedStyle(stickyEl).top);
        const stickyTop = Number.isNaN(parsedTop) ? 96 : parsedTop;
        const travel = Math.max(1, wrapEl.offsetHeight - stickyEl.offsetHeight);
        const passed = stickyTop - wrapEl.getBoundingClientRect().top;
        const pct = Math.min(0.9999, Math.max(0, passed / travel));
        const index = Math.floor(pct * N);

        if (index >= 0 && index < N && index !== activeRef.current) {
          setActive(index);
        }
      }

      requestAnimationFrame(poll);
    };

    const frame = requestAnimationFrame(poll);

    return () => {
      running = false;
      cancelAnimationFrame(frame);
    };
  }, []);

  const job = jobs[active];

  return (
    <Section className={styles.root}>
      <div className={styles.scrollWrap} ref={scrollWrapRef} style={{ '--steps': N } as CSSProperties}>
        <div className={styles.sticky} ref={stickyRef}>
          <div className={styles.head}>
            <TitleBlock
              title={t('Four operations, one register.')}
              description={t(
                'Run them once at onboarding, then leave them running. Each returns the same structure through the platform, the API and the bulk file.',
              )}
              align="left"
            />
          </div>

          <div className={styles.grid}>
            <div className={styles.nav} role="tablist" aria-label="Supplier operations">
              {jobs.map((item, i) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={active === i}
                  id={`job-tab-${item.id}`}
                  aria-controls={`job-panel-${item.id}`}
                  className={styles.tab}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  onClick={() => pick(i, true)}
                  onKeyDown={(event) => onKeyDown(event, i)}
                  key={item.id}
                >
                  <span className={styles.tabIx}>{String(i + 1).padStart(2, '0')}</span>
                  <span className={styles.tabTitle}>{item.title}</span>
                </button>
              ))}
            </div>

            <div className={styles.panelWrap}>
              <div
                className={styles.panel}
                id={`job-panel-${job.id}`}
                role="tabpanel"
                aria-labelledby={`job-tab-${job.id}`}
                key={job.id}
              >
                <span className={styles.panelPn}>{String(active + 1).padStart(2, '0')}</span>
                <h3 className={styles.panelTitle}>{job.title}</h3>
                <p className={styles.panelDesc}>{job.description}</p>

                <div className={styles.fields}>
                  <b>{t('Returned')}</b>
                  <span>{job.returned}</span>
                </div>

                <div className={styles.demo}>
                  <div className={styles.demoHead}>
                    <span>{job.title}</span>
                    <span className={styles.demoTag}>{t('Sample')}</span>
                  </div>
                  <div className={styles.demoBody}>
                    <JobDemo demo={job.demo} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
